import type { ListedConnector } from "../../../api/connectorsApi";
import { routeChoicesOf } from "../../threads/hosting/routeChoices";

/**
 * How far the guided connector setup got (onboarding.md OB-1, OB-2), derived from facts the
 * server already reports and never stored: the server address, the person's PC connectors, their
 * presence and their latest capability reports. One function, so the guide, the connect button
 * and the glow never disagree. A person's "DMW server" host is not a PC and never counts
 * (ADR-0023, PR-1).
 *
 * A route is usable by the rule the route picker uses (`routeChoicesOf`), so the guide calls a PC
 * ready exactly when the picker would offer one of its routes.
 */

/** The required steps, in order; the welcome and the end screen are views, not steps. */
export const SETUP_STEPS = ["server_address", "pair", "start", "agents"] as const;
export type SetupStepId = (typeof SETUP_STEPS)[number];

/** The first required step that is not done, or `ready`. */
export type SetupPhase =
  | "needs_server_address"
  | "waiting_for_server_address"
  | "needs_pairing"
  | "needs_first_report"
  | "needs_route"
  | "ready";

export type SetupStepStatus = "done" | "current" | "to_do" | "waiting_for_administrator" | "not_applicable";

export interface SetupFacts {
  readonly isAdministrator: boolean;
  readonly serverAddressConfigured: boolean;
  readonly connectors: readonly ListedConnector[];
}

export interface SetupProgress {
  readonly phase: SetupPhase;
  /** The required steps are done (OB-1's "Setup complete"). */
  readonly complete: boolean;
  readonly steps: Readonly<Record<SetupStepId, SetupStepStatus>>;
  /** A connector of the person's own PC is connected now. */
  readonly pcConnectorOnline: boolean;
  /** Any connector of theirs is connected, a "DMW server" host included: something can run. */
  readonly anyConnectorOnline: boolean;
}

/** The person's active PC connectors: not revoked, not a server host. */
export function pcConnectorsOf(connectors: readonly ListedConnector[]): ListedConnector[] {
  return connectors.filter((connector) => connector.state === "active" && !connector.hostedByServer);
}

/** The person's PCs that have reported their agents and routes: until one has, the setup waits for the PC. */
export function reportedPcsOf(connectors: readonly ListedConnector[]): ListedConnector[] {
  return pcConnectorsOf(connectors).filter((connector) => connector.status.reportReceivedAt !== null);
}

function hasUsableRoute(connector: ListedConnector): boolean {
  return routeChoicesOf(connector).some((choice) => choice.unavailableBecause === null);
}

export function setupProgressOf(facts: SetupFacts): SetupProgress {
  const pcs = pcConnectorsOf(facts.connectors);
  const reported = reportedPcsOf(facts.connectors);
  const phase: SetupPhase = !facts.serverAddressConfigured
    ? facts.isAdministrator
      ? "needs_server_address"
      : "waiting_for_server_address"
    : pcs.length === 0
      ? "needs_pairing"
      : reported.length === 0
        ? "needs_first_report"
        : !reported.some(hasUsableRoute)
          ? "needs_route"
          : "ready";
  return {
    phase,
    complete: phase === "ready",
    steps: stepStatusesOf(phase, facts.serverAddressConfigured),
    pcConnectorOnline: pcs.some((connector) => connector.status.online),
    anyConnectorOnline: facts.connectors.some((connector) => connector.state === "active" && connector.status.online),
  };
}

const PHASE_STEP: Readonly<Record<SetupPhase, SetupStepId | null>> = {
  needs_server_address: "server_address",
  waiting_for_server_address: "server_address",
  needs_pairing: "pair",
  needs_first_report: "start",
  needs_route: "agents",
  ready: null,
};

function stepStatusesOf(phase: SetupPhase, serverAddressConfigured: boolean): Record<SetupStepId, SetupStepStatus> {
  const current = PHASE_STEP[phase];
  const currentIndex = current === null ? SETUP_STEPS.length : SETUP_STEPS.indexOf(current);
  const statuses = {} as Record<SetupStepId, SetupStepStatus>;
  SETUP_STEPS.forEach((step, index) => {
    statuses[step] = index < currentIndex ? "done" : index === currentIndex ? "current" : "to_do";
  });
  // The address is an administrator's step: once set, members never see it; while missing, they wait.
  if (serverAddressConfigured) statuses.server_address = "not_applicable";
  if (phase === "waiting_for_server_address") statuses.server_address = "waiting_for_administrator";
  return statuses;
}

/** The step a view follows: the current one, or the end screen once the setup is complete. */
export function currentStepOf(progress: SetupProgress): SetupStepId | null {
  return PHASE_STEP[progress.phase];
}
